import { useSWRMutation } from '@tiger/swr';
import { useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import Toast from 'app/components/Toast';
import useDebounceFn from 'app/hooks/useDebounceFn';
import API from 'app/utils/API';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import { PeriodTypeEnum } from './types';

interface NextDepositDateTipProps {
    periodType: PeriodTypeEnum;
    isIRA?: boolean;
}

export const NextDepositDateTip = ({ periodType, isIRA }: NextDepositDateTipProps) => {
    const abortControllerRef = useRef<AbortController | null>(null);

    const { data, trigger, error } = useSWRMutation(
        API.TRADE.funding.getNextExecuteTime(),
        (params: { period_type: PeriodTypeEnum }) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            return http.get(API.TRADE.funding.getNextExecuteTime(), {
                params: {
                    bank: 'BMO',
                    period_type: params.period_type
                },
                signal: abortControllerRef.current?.signal
            });
        },
        {
            onError: error => {
                Toast.show(error);
            }
        }
    );

    const { run: handlePeriodChange } = useDebounceFn(
        (value: PeriodTypeEnum) => {
            if (value && value !== PeriodTypeEnum.NONE) {
                trigger({ period_type: value });
            } else {
                abortControllerRef.current?.abort();
            }
        },
        { wait: 300 }
    );

    useEffect(() => {
        handlePeriodChange(periodType);
    }, [periodType, handlePeriodChange]);

    const helpHtml = useMemo(() => {
        const noneTpl = isIRA
            ? __('Next contribution date: None. The next contribution will need you to submit your request manually.')
            : __('Next deposit date: None. The next deposit will need you to submit your request manually.');
        const otherTpl = isIRA
            ? __('Next contribution date: %s. The system will generate the contribution automatically.')
            : __('Next deposit date: %s. The system will generate the deposit automatically.');

        // 如果有API返回的真实时间，使用API数据，否则使用计算的时间
        let nextDate: string | undefined;

        if (data?.data) {
            nextDate = dayjs(data.data).format('YYYY-MM-DD');
        }

        return periodType === PeriodTypeEnum.NONE || !nextDate || error
            ? noneTpl
            : printf(otherTpl, `<span class="time-tips">${nextDate}</span>`);
    }, [isIRA, periodType, data, error]);

    return <div style={{ color: '#95a1b7' }} dangerouslySetInnerHTML={{ __html: helpHtml }} />;
};
